/* eslint-disable */
import { Recipient } from "../../../api/common/common";
import {
  SalesOrder_PaymentType,
  salesOrder_PaymentTypeFromJSON,
  salesOrder_PaymentTypeToJSON,
} from "../../../api/orders/resources";
import { Timestamp } from "../../../google/protobuf/timestamp";

export const protobufPackage = "store.api";

/** ProductSaleState defines the sale state the product is in. */
export enum ProductSaleState {
  ON_SALE = 0,
  /** COMING_SOON - 即将上架, product state in `PACKAGING` (打包中), `PUBLISH_REVIEW` (上架待审核), or `PUBLISH_RESERVED` (预约上架) */
  COMING_SOON = 1,
  /**
   * SOLD_OUT - 商品已售, product state in `STATE_UNSPECIFIED` and any sku in `PUBLISHED` (上架中) and (have stock or
   * have in-stock reminder)
   */
  SOLD_OUT = 2,
}

export function productSaleStateFromJSON(object: any): ProductSaleState {
  switch (object) {
    case 0:
    case "ON_SALE":
      return ProductSaleState.ON_SALE;
    case 1:
    case "COMING_SOON":
      return ProductSaleState.COMING_SOON;
    case 2:
    case "SOLD_OUT":
      return ProductSaleState.SOLD_OUT;
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum ProductSaleState");
  }
}

export function productSaleStateToJSON(object: ProductSaleState): string {
  switch (object) {
    case ProductSaleState.ON_SALE:
      return "ON_SALE";
    case ProductSaleState.COMING_SOON:
      return "COMING_SOON";
    case ProductSaleState.SOLD_OUT:
      return "SOLD_OUT";
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum ProductSaleState");
  }
}

/** Anchor defines the anchor position and direction. */
export interface Anchor {
  direction: Anchor_Direction;
  /** percentage in x-direction [0, 100] */
  pX: number;
  /** percentage in y-direction [0, 100] */
  pY: number;
}

export enum Anchor_Direction {
  DOWN = 0,
  UP = 1,
}

export function anchor_DirectionFromJSON(object: any): Anchor_Direction {
  switch (object) {
    case 0:
    case "DOWN":
      return Anchor_Direction.DOWN;
    case 1:
    case "UP":
      return Anchor_Direction.UP;
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum Anchor_Direction");
  }
}

export function anchor_DirectionToJSON(object: Anchor_Direction): string {
  switch (object) {
    case Anchor_Direction.DOWN:
      return "DOWN";
    case Anchor_Direction.UP:
      return "UP";
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum Anchor_Direction");
  }
}

/** PercentagePoint defines a coordinate point in terms of percentage of its respective parent. */
export interface PercentagePoint {
  /** percentage in x-direction [0, 100] */
  pX: number;
  /** percentage in y-direction [0, 100] */
  pY: number;
}

/** Image defines an image with its aspect ratio. */
export interface Image {
  hash: string;
  /** h/w round to two decimal points */
  aspectRatio: number;
}

/** ProductMeta defines the required meta for a product. */
export interface ProductMeta {
  /** product id */
  productId: string;
  /** product name */
  name: string;
  /** original price */
  originPrice: string;
  /** promotion price */
  promotionPrice: string;
  /** first product image with `TYPE_OUTFIT`; otherwise, `TYPE_LIST` */
  image:
    | Image
    | undefined;
  /** big category */
  big:
    | Category
    | undefined;
  /** used for sorting when category of shop window is `ALL` */
  sequence: number;
}

/** Rating defines a client rating of a product. */
export interface Rating {
  /** Output only. Rating id. */
  id: string;
  /** Output only. Product id. */
  productId: string;
  /** Output only. Product sku id. */
  skuId: string;
  /** Output only. customer order id */
  orderId: string;
  /** Output only. Rating create time. */
  createTime:
    | Date
    | undefined;
  /** Output only. Reply from customer service. */
  reply: string;
  /** Output only. Reply from customer service. */
  replyTime:
    | Date
    | undefined;
  /** Required. Rating content. */
  content: string;
}

/** Product sku info. */
export interface Rating_SkuInfo {
  /** Color image 360x360 resized 90x90 if exist; otherwise, 360x360 */
  image: string;
  /** Color name */
  color: string;
  /** Size name */
  size: string;
}

/** Color defines a color visual and its name. */
export interface Color {
  /** color id, the color id of the sku */
  id: string;
  /** color name */
  name: string;
  hex?: string | undefined;
  thumbnail?: string | undefined;
  image360: string;
}

export interface Size {
  name: string;
  stockRemind: boolean;
  skuId: string;
  /** sizes of the color inventory. */
  num: number;
  /** 尺码后缀 */
  suffix: string;
}

/** Promotion defines a product promotion. */
export interface Promotion {
  /** promotion id */
  id: string;
  /** promotion name */
  name: string;
  /** promotion enabled, indicates whether the promotion exists in sales event */
  enabled: boolean;
}

/** Paragraph defines a text section that contains title and description. */
export interface Paragraph {
  title: string;
  description: string;
}

/** SplitColor define a color and its list hash. */
export interface SplitColor {
  /** hash of split color list hash. 商品列表圖 width 800px. */
  listHash: string;
  /** hash of split color cloth hash. 商品圖 500px. */
  clothHash: string;
  /** colors of the product thumbnail size 24 x 24 px */
  color:
    | Color
    | undefined;
  /** sizes of the color. */
  sizes: Size[];
}

/** ProductColorItem defines a product item which contains color information. */
export interface ProductColorItem {
  /** aspect ratio of the list image, h / w */
  listAspectRatio: number;
  /** aspect ratio of the cloth image in SplitColor, h / w */
  clothAspectRatio: number;
  /** product contents */
  items: ProductColorItem_Item[];
}

export interface ProductColorItem_Item {
  /** product meta */
  meta:
    | ProductMeta
    | undefined;
  /**
   * image hash of 商品列表圖 width 800px, if absent, using 商品圖 360px.
   * If this is empty, means the product colors need to be split, using split colors instead.
   */
  listHash: string;
  /** Always empty from api. use in frontend for mapping split color cloth_hash when product colors need to be split. */
  clothHash: string;
  /** color information */
  colors: SplitColor[];
}

/** Segmented link. */
export interface SegmentedLink {
  /** Redirect link. */
  link?:
    | string
    | undefined;
  /** Valid if the content of the image is a product. */
  meta?:
    | ProductMeta
    | undefined;
  /** Top left point of the segment. */
  tlPoint:
    | PercentagePoint
    | undefined;
  /** Bottom right point of the segment. */
  brPoint: PercentagePoint | undefined;
}

export interface SegmentedItem {
  /** item image. */
  image:
    | Image
    | undefined;
  /** redirect link. */
  link?:
    | string
    | undefined;
  /** valid if the content of the image is a product. */
  meta?:
    | ProductMeta
    | undefined;
  /** segmented links */
  segmented?: SegmentedItem_SegmentedLinks | undefined;
}

export interface SegmentedItem_SegmentedLinks {
  links: SegmentedLink[];
}

export interface Category {
  /** category name */
  name: string;
  /** category english name */
  nameEn: string;
}

export interface App {
}

/** Platform defines the platform for client. 平台 */
export enum App_Platform {
  UNKNOWN = 0,
  WEB_DESKTOP = 1,
  WEB_MOBILE = 2,
  ANDROID = 4,
  IOS = 8,
}

export function app_PlatformFromJSON(object: any): App_Platform {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return App_Platform.UNKNOWN;
    case 1:
    case "WEB_DESKTOP":
      return App_Platform.WEB_DESKTOP;
    case 2:
    case "WEB_MOBILE":
      return App_Platform.WEB_MOBILE;
    case 4:
    case "ANDROID":
      return App_Platform.ANDROID;
    case 8:
    case "IOS":
      return App_Platform.IOS;
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum App_Platform");
  }
}

export function app_PlatformToJSON(object: App_Platform): string {
  switch (object) {
    case App_Platform.UNKNOWN:
      return "UNKNOWN";
    case App_Platform.WEB_DESKTOP:
      return "WEB_DESKTOP";
    case App_Platform.WEB_MOBILE:
      return "WEB_MOBILE";
    case App_Platform.ANDROID:
      return "ANDROID";
    case App_Platform.IOS:
      return "IOS";
    default:
      throw new tsProtoGlobalThis.Error("Unrecognized enum value " + object + " for enum App_Platform");
  }
}

/** 付款與運送方式 */
export interface PaymentShippingInfo {
  /** 付款類型 */
  type: SalesOrder_PaymentType;
  /**
   * 付款方式, from settings
   * KeyCheckoutSevenShippingAndPaymentMethod
   * KeyCheckoutFamilyShippingAndPaymentMethod
   * KeyCheckoutCreditCardShippingAndPaymentMethod
   * KeyCheckoutLinePayShippingAndPaymentMethod
   * KeyCheckoutDeliveryShippingAndPaymentMethod
   */
  desc?:
    | string
    | undefined;
  /**
   * 付款圖示
   * Apple Pay
   */
  image?:
    | Image
    | undefined;
  /** 免運門檻, 有值代表有免運優惠, 沒值代表沒有免運優惠 */
  threshold: string;
  /**
   * 免運門檻差額
   * (差額 <= 0): 滿 threshold 元免運
   * (差額 > 0): 再購買 threshold_diff 元免運
   */
  thresholdDiff: string;
  /** 原始運費 */
  fee: string;
  /** 優惠運費 */
  promotionFee: string;
  /**
   * 超過限制的提示文案
   * 依序判斷下列項目的限制, 如果有超過就會填入對應文案, 一有超過就不需要再判斷剩下項目
   * 1. 材積
   * 2. 重量
   * 3. 金額
   * 各個 付款與運送方式 會有不同的限制及內容, 文案的字型會分一般顏色及紅色, 會有 HTML Tag
   */
  overLimitRemind: string;
  /**
   * 未取次數提醒文案
   * 如果此方式有未取次數, 會填入對應文案
   */
  noPickUpRemind: string;
  /** 藉由小計及免運門檻判別後的運費 */
  actualFee: string;
  /** 應付金額 = 商品原價 - 活動折扣 + 運費 */
  total: string;
  /** 應付金額 USD */
  totalUsd: string;
}

/** 付款與運送方式-海外地區 */
export interface PaymentShippingOverseaInfo {
  /** 地區名稱 */
  name: string;
  /** 地區英文名稱 */
  englishName: string;
  /** 電話國碼 */
  phoneCountryCode: string;
  /** 付款與運送方式 */
  info: PaymentShippingInfo | undefined;
}

/** 橫幅公告/置頂公告 */
export interface Announcement {
  id: string;
  /** redirect link. valid for TYPE_BANNER only. */
  link: string;
  /** content */
  content: string;
}

/** 常用收件資訊 */
export interface RecipientAddress {
  /**
   * Optional. 常用收件資訊 id
   * Required when update.
   */
  id: string;
  /** Required. 收件資訊 */
  recipient:
    | Recipient
    | undefined;
  /** Required. 設為默認 */
  isDefault: boolean;
}

function createBaseAnchor(): Anchor {
  return { direction: 0, pX: 0, pY: 0 };
}

export const Anchor = {
  fromJSON(object: any): Anchor {
    return {
      direction: isSet(object.direction) ? anchor_DirectionFromJSON(object.direction) : 0,
      pX: isSet(object.pX) ? Number(object.pX) : 0,
      pY: isSet(object.pY) ? Number(object.pY) : 0,
    };
  },

  toJSON(message: Anchor): unknown {
    const obj: any = {};
    message.direction !== undefined && (obj.direction = anchor_DirectionToJSON(message.direction));
    message.pX !== undefined && (obj.pX = Math.round(message.pX));
    message.pY !== undefined && (obj.pY = Math.round(message.pY));
    return obj;
  },

  create<I extends Exact<DeepPartial<Anchor>, I>>(base?: I): Anchor {
    return Anchor.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Anchor>, I>>(object: I): Anchor {
    const message = createBaseAnchor();
    message.direction = object.direction ?? 0;
    message.pX = object.pX ?? 0;
    message.pY = object.pY ?? 0;
    return message;
  },
};

function createBasePercentagePoint(): PercentagePoint {
  return { pX: 0, pY: 0 };
}

export const PercentagePoint = {
  fromJSON(object: any): PercentagePoint {
    return { pX: isSet(object.pX) ? Number(object.pX) : 0, pY: isSet(object.pY) ? Number(object.pY) : 0 };
  },

  toJSON(message: PercentagePoint): unknown {
    const obj: any = {};
    message.pX !== undefined && (obj.pX = Math.round(message.pX));
    message.pY !== undefined && (obj.pY = Math.round(message.pY));
    return obj;
  },

  create<I extends Exact<DeepPartial<PercentagePoint>, I>>(base?: I): PercentagePoint {
    return PercentagePoint.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<PercentagePoint>, I>>(object: I): PercentagePoint {
    const message = createBasePercentagePoint();
    message.pX = object.pX ?? 0;
    message.pY = object.pY ?? 0;
    return message;
  },
};

function createBaseImage(): Image {
  return { hash: "", aspectRatio: 0 };
}

export const Image = {
  fromJSON(object: any): Image {
    return {
      hash: isSet(object.hash) ? String(object.hash) : "",
      aspectRatio: isSet(object.aspectRatio) ? Number(object.aspectRatio) : 0,
    };
  },

  toJSON(message: Image): unknown {
    const obj: any = {};
    message.hash !== undefined && (obj.hash = message.hash);
    message.aspectRatio !== undefined && (obj.aspectRatio = message.aspectRatio);
    return obj;
  },

  create<I extends Exact<DeepPartial<Image>, I>>(base?: I): Image {
    return Image.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Image>, I>>(object: I): Image {
    const message = createBaseImage();
    message.hash = object.hash ?? "";
    message.aspectRatio = object.aspectRatio ?? 0;
    return message;
  },
};

function createBaseProductMeta(): ProductMeta {
  return {
    productId: "",
    name: "",
    originPrice: "",
    promotionPrice: "",
    image: undefined,
    big: undefined,
    sequence: 0,
  };
}

export const ProductMeta = {
  fromJSON(object: any): ProductMeta {
    return {
      productId: isSet(object.productId) ? String(object.productId) : "",
      name: isSet(object.name) ? String(object.name) : "",
      originPrice: isSet(object.originPrice) ? String(object.originPrice) : "",
      promotionPrice: isSet(object.promotionPrice) ? String(object.promotionPrice) : "",
      image: isSet(object.image) ? Image.fromJSON(object.image) : undefined,
      big: isSet(object.big) ? Category.fromJSON(object.big) : undefined,
      sequence: isSet(object.sequence) ? Number(object.sequence) : 0,
    };
  },

  toJSON(message: ProductMeta): unknown {
    const obj: any = {};
    message.productId !== undefined && (obj.productId = message.productId);
    message.name !== undefined && (obj.name = message.name);
    message.originPrice !== undefined && (obj.originPrice = message.originPrice);
    message.promotionPrice !== undefined && (obj.promotionPrice = message.promotionPrice);
    message.image !== undefined && (obj.image = message.image ? Image.toJSON(message.image) : undefined);
    message.big !== undefined && (obj.big = message.big ? Category.toJSON(message.big) : undefined);
    message.sequence !== undefined && (obj.sequence = Math.round(message.sequence));
    return obj;
  },

  create<I extends Exact<DeepPartial<ProductMeta>, I>>(base?: I): ProductMeta {
    return ProductMeta.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<ProductMeta>, I>>(object: I): ProductMeta {
    const message = createBaseProductMeta();
    message.productId = object.productId ?? "";
    message.name = object.name ?? "";
    message.originPrice = object.originPrice ?? "";
    message.promotionPrice = object.promotionPrice ?? "";
    message.image = (object.image !== undefined && object.image !== null) ? Image.fromPartial(object.image) : undefined;
    message.big = (object.big !== undefined && object.big !== null) ? Category.fromPartial(object.big) : undefined;
    message.sequence = object.sequence ?? 0;
    return message;
  },
};

function createBaseRating(): Rating {
  return {
    id: "",
    productId: "",
    skuId: "",
    orderId: "",
    createTime: undefined,
    reply: "",
    replyTime: undefined,
    content: "",
  };
}

export const Rating = {
  fromJSON(object: any): Rating {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      productId: isSet(object.productId) ? String(object.productId) : "",
      skuId: isSet(object.skuId) ? String(object.skuId) : "",
      orderId: isSet(object.orderId) ? String(object.orderId) : "",
      createTime: isSet(object.createTime) ? fromJsonTimestamp(object.createTime) : undefined,
      reply: isSet(object.reply) ? String(object.reply) : "",
      replyTime: isSet(object.replyTime) ? fromJsonTimestamp(object.replyTime) : undefined,
      content: isSet(object.content) ? String(object.content) : "",
    };
  },

  toJSON(message: Rating): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.productId !== undefined && (obj.productId = message.productId);
    message.skuId !== undefined && (obj.skuId = message.skuId);
    message.orderId !== undefined && (obj.orderId = message.orderId);
    message.createTime !== undefined && (obj.createTime = message.createTime.toISOString());
    message.reply !== undefined && (obj.reply = message.reply);
    message.replyTime !== undefined && (obj.replyTime = message.replyTime.toISOString());
    message.content !== undefined && (obj.content = message.content);
    return obj;
  },

  create<I extends Exact<DeepPartial<Rating>, I>>(base?: I): Rating {
    return Rating.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Rating>, I>>(object: I): Rating {
    const message = createBaseRating();
    message.id = object.id ?? "";
    message.productId = object.productId ?? "";
    message.skuId = object.skuId ?? "";
    message.orderId = object.orderId ?? "";
    message.createTime = object.createTime ?? undefined;
    message.reply = object.reply ?? "";
    message.replyTime = object.replyTime ?? undefined;
    message.content = object.content ?? "";
    return message;
  },
};

function createBaseRating_SkuInfo(): Rating_SkuInfo {
  return { image: "", color: "", size: "" };
}

export const Rating_SkuInfo = {
  fromJSON(object: any): Rating_SkuInfo {
    return {
      image: isSet(object.image) ? String(object.image) : "",
      color: isSet(object.color) ? String(object.color) : "",
      size: isSet(object.size) ? String(object.size) : "",
    };
  },

  toJSON(message: Rating_SkuInfo): unknown {
    const obj: any = {};
    message.image !== undefined && (obj.image = message.image);
    message.color !== undefined && (obj.color = message.color);
    message.size !== undefined && (obj.size = message.size);
    return obj;
  },

  create<I extends Exact<DeepPartial<Rating_SkuInfo>, I>>(base?: I): Rating_SkuInfo {
    return Rating_SkuInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Rating_SkuInfo>, I>>(object: I): Rating_SkuInfo {
    const message = createBaseRating_SkuInfo();
    message.image = object.image ?? "";
    message.color = object.color ?? "";
    message.size = object.size ?? "";
    return message;
  },
};

function createBaseColor(): Color {
  return { id: "", name: "", hex: undefined, thumbnail: undefined, image360: "" };
}

export const Color = {
  fromJSON(object: any): Color {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      name: isSet(object.name) ? String(object.name) : "",
      hex: isSet(object.hex) ? String(object.hex) : undefined,
      thumbnail: isSet(object.thumbnail) ? String(object.thumbnail) : undefined,
      image360: isSet(object.image360) ? String(object.image360) : "",
    };
  },

  toJSON(message: Color): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.name !== undefined && (obj.name = message.name);
    message.hex !== undefined && (obj.hex = message.hex);
    message.thumbnail !== undefined && (obj.thumbnail = message.thumbnail);
    message.image360 !== undefined && (obj.image360 = message.image360);
    return obj;
  },

  create<I extends Exact<DeepPartial<Color>, I>>(base?: I): Color {
    return Color.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Color>, I>>(object: I): Color {
    const message = createBaseColor();
    message.id = object.id ?? "";
    message.name = object.name ?? "";
    message.hex = object.hex ?? undefined;
    message.thumbnail = object.thumbnail ?? undefined;
    message.image360 = object.image360 ?? "";
    return message;
  },
};

function createBaseSize(): Size {
  return { name: "", stockRemind: false, skuId: "", num: 0, suffix: "" };
}

export const Size = {
  fromJSON(object: any): Size {
    return {
      name: isSet(object.name) ? String(object.name) : "",
      stockRemind: isSet(object.stockRemind) ? Boolean(object.stockRemind) : false,
      skuId: isSet(object.skuId) ? String(object.skuId) : "",
      num: isSet(object.num) ? Number(object.num) : 0,
      suffix: isSet(object.suffix) ? String(object.suffix) : "",
    };
  },

  toJSON(message: Size): unknown {
    const obj: any = {};
    message.name !== undefined && (obj.name = message.name);
    message.stockRemind !== undefined && (obj.stockRemind = message.stockRemind);
    message.skuId !== undefined && (obj.skuId = message.skuId);
    message.num !== undefined && (obj.num = Math.round(message.num));
    message.suffix !== undefined && (obj.suffix = message.suffix);
    return obj;
  },

  create<I extends Exact<DeepPartial<Size>, I>>(base?: I): Size {
    return Size.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Size>, I>>(object: I): Size {
    const message = createBaseSize();
    message.name = object.name ?? "";
    message.stockRemind = object.stockRemind ?? false;
    message.skuId = object.skuId ?? "";
    message.num = object.num ?? 0;
    message.suffix = object.suffix ?? "";
    return message;
  },
};

function createBasePromotion(): Promotion {
  return { id: "", name: "", enabled: false };
}

export const Promotion = {
  fromJSON(object: any): Promotion {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      name: isSet(object.name) ? String(object.name) : "",
      enabled: isSet(object.enabled) ? Boolean(object.enabled) : false,
    };
  },

  toJSON(message: Promotion): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.name !== undefined && (obj.name = message.name);
    message.enabled !== undefined && (obj.enabled = message.enabled);
    return obj;
  },

  create<I extends Exact<DeepPartial<Promotion>, I>>(base?: I): Promotion {
    return Promotion.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Promotion>, I>>(object: I): Promotion {
    const message = createBasePromotion();
    message.id = object.id ?? "";
    message.name = object.name ?? "";
    message.enabled = object.enabled ?? false;
    return message;
  },
};

function createBaseParagraph(): Paragraph {
  return { title: "", description: "" };
}

export const Paragraph = {
  fromJSON(object: any): Paragraph {
    return {
      title: isSet(object.title) ? String(object.title) : "",
      description: isSet(object.description) ? String(object.description) : "",
    };
  },

  toJSON(message: Paragraph): unknown {
    const obj: any = {};
    message.title !== undefined && (obj.title = message.title);
    message.description !== undefined && (obj.description = message.description);
    return obj;
  },

  create<I extends Exact<DeepPartial<Paragraph>, I>>(base?: I): Paragraph {
    return Paragraph.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Paragraph>, I>>(object: I): Paragraph {
    const message = createBaseParagraph();
    message.title = object.title ?? "";
    message.description = object.description ?? "";
    return message;
  },
};

function createBaseSplitColor(): SplitColor {
  return { listHash: "", clothHash: "", color: undefined, sizes: [] };
}

export const SplitColor = {
  fromJSON(object: any): SplitColor {
    return {
      listHash: isSet(object.listHash) ? String(object.listHash) : "",
      clothHash: isSet(object.clothHash) ? String(object.clothHash) : "",
      color: isSet(object.color) ? Color.fromJSON(object.color) : undefined,
      sizes: Array.isArray(object?.sizes) ? object.sizes.map((e: any) => Size.fromJSON(e)) : [],
    };
  },

  toJSON(message: SplitColor): unknown {
    const obj: any = {};
    message.listHash !== undefined && (obj.listHash = message.listHash);
    message.clothHash !== undefined && (obj.clothHash = message.clothHash);
    message.color !== undefined && (obj.color = message.color ? Color.toJSON(message.color) : undefined);
    if (message.sizes) {
      obj.sizes = message.sizes.map((e) => e ? Size.toJSON(e) : undefined);
    } else {
      obj.sizes = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SplitColor>, I>>(base?: I): SplitColor {
    return SplitColor.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SplitColor>, I>>(object: I): SplitColor {
    const message = createBaseSplitColor();
    message.listHash = object.listHash ?? "";
    message.clothHash = object.clothHash ?? "";
    message.color = (object.color !== undefined && object.color !== null) ? Color.fromPartial(object.color) : undefined;
    message.sizes = object.sizes?.map((e) => Size.fromPartial(e)) || [];
    return message;
  },
};

function createBaseProductColorItem(): ProductColorItem {
  return { listAspectRatio: 0, clothAspectRatio: 0, items: [] };
}

export const ProductColorItem = {
  fromJSON(object: any): ProductColorItem {
    return {
      listAspectRatio: isSet(object.listAspectRatio) ? Number(object.listAspectRatio) : 0,
      clothAspectRatio: isSet(object.clothAspectRatio) ? Number(object.clothAspectRatio) : 0,
      items: Array.isArray(object?.items) ? object.items.map((e: any) => ProductColorItem_Item.fromJSON(e)) : [],
    };
  },

  toJSON(message: ProductColorItem): unknown {
    const obj: any = {};
    message.listAspectRatio !== undefined && (obj.listAspectRatio = message.listAspectRatio);
    message.clothAspectRatio !== undefined && (obj.clothAspectRatio = message.clothAspectRatio);
    if (message.items) {
      obj.items = message.items.map((e) => e ? ProductColorItem_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProductColorItem>, I>>(base?: I): ProductColorItem {
    return ProductColorItem.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<ProductColorItem>, I>>(object: I): ProductColorItem {
    const message = createBaseProductColorItem();
    message.listAspectRatio = object.listAspectRatio ?? 0;
    message.clothAspectRatio = object.clothAspectRatio ?? 0;
    message.items = object.items?.map((e) => ProductColorItem_Item.fromPartial(e)) || [];
    return message;
  },
};

function createBaseProductColorItem_Item(): ProductColorItem_Item {
  return { meta: undefined, listHash: "", clothHash: "", colors: [] };
}

export const ProductColorItem_Item = {
  fromJSON(object: any): ProductColorItem_Item {
    return {
      meta: isSet(object.meta) ? ProductMeta.fromJSON(object.meta) : undefined,
      listHash: isSet(object.listHash) ? String(object.listHash) : "",
      clothHash: isSet(object.clothHash) ? String(object.clothHash) : "",
      colors: Array.isArray(object?.colors) ? object.colors.map((e: any) => SplitColor.fromJSON(e)) : [],
    };
  },

  toJSON(message: ProductColorItem_Item): unknown {
    const obj: any = {};
    message.meta !== undefined && (obj.meta = message.meta ? ProductMeta.toJSON(message.meta) : undefined);
    message.listHash !== undefined && (obj.listHash = message.listHash);
    message.clothHash !== undefined && (obj.clothHash = message.clothHash);
    if (message.colors) {
      obj.colors = message.colors.map((e) => e ? SplitColor.toJSON(e) : undefined);
    } else {
      obj.colors = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProductColorItem_Item>, I>>(base?: I): ProductColorItem_Item {
    return ProductColorItem_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<ProductColorItem_Item>, I>>(object: I): ProductColorItem_Item {
    const message = createBaseProductColorItem_Item();
    message.meta = (object.meta !== undefined && object.meta !== null)
      ? ProductMeta.fromPartial(object.meta)
      : undefined;
    message.listHash = object.listHash ?? "";
    message.clothHash = object.clothHash ?? "";
    message.colors = object.colors?.map((e) => SplitColor.fromPartial(e)) || [];
    return message;
  },
};

function createBaseSegmentedLink(): SegmentedLink {
  return { link: undefined, meta: undefined, tlPoint: undefined, brPoint: undefined };
}

export const SegmentedLink = {
  fromJSON(object: any): SegmentedLink {
    return {
      link: isSet(object.link) ? String(object.link) : undefined,
      meta: isSet(object.meta) ? ProductMeta.fromJSON(object.meta) : undefined,
      tlPoint: isSet(object.tlPoint) ? PercentagePoint.fromJSON(object.tlPoint) : undefined,
      brPoint: isSet(object.brPoint) ? PercentagePoint.fromJSON(object.brPoint) : undefined,
    };
  },

  toJSON(message: SegmentedLink): unknown {
    const obj: any = {};
    message.link !== undefined && (obj.link = message.link);
    message.meta !== undefined && (obj.meta = message.meta ? ProductMeta.toJSON(message.meta) : undefined);
    message.tlPoint !== undefined &&
      (obj.tlPoint = message.tlPoint ? PercentagePoint.toJSON(message.tlPoint) : undefined);
    message.brPoint !== undefined &&
      (obj.brPoint = message.brPoint ? PercentagePoint.toJSON(message.brPoint) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<SegmentedLink>, I>>(base?: I): SegmentedLink {
    return SegmentedLink.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SegmentedLink>, I>>(object: I): SegmentedLink {
    const message = createBaseSegmentedLink();
    message.link = object.link ?? undefined;
    message.meta = (object.meta !== undefined && object.meta !== null)
      ? ProductMeta.fromPartial(object.meta)
      : undefined;
    message.tlPoint = (object.tlPoint !== undefined && object.tlPoint !== null)
      ? PercentagePoint.fromPartial(object.tlPoint)
      : undefined;
    message.brPoint = (object.brPoint !== undefined && object.brPoint !== null)
      ? PercentagePoint.fromPartial(object.brPoint)
      : undefined;
    return message;
  },
};

function createBaseSegmentedItem(): SegmentedItem {
  return { image: undefined, link: undefined, meta: undefined, segmented: undefined };
}

export const SegmentedItem = {
  fromJSON(object: any): SegmentedItem {
    return {
      image: isSet(object.image) ? Image.fromJSON(object.image) : undefined,
      link: isSet(object.link) ? String(object.link) : undefined,
      meta: isSet(object.meta) ? ProductMeta.fromJSON(object.meta) : undefined,
      segmented: isSet(object.segmented) ? SegmentedItem_SegmentedLinks.fromJSON(object.segmented) : undefined,
    };
  },

  toJSON(message: SegmentedItem): unknown {
    const obj: any = {};
    message.image !== undefined && (obj.image = message.image ? Image.toJSON(message.image) : undefined);
    message.link !== undefined && (obj.link = message.link);
    message.meta !== undefined && (obj.meta = message.meta ? ProductMeta.toJSON(message.meta) : undefined);
    message.segmented !== undefined &&
      (obj.segmented = message.segmented ? SegmentedItem_SegmentedLinks.toJSON(message.segmented) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<SegmentedItem>, I>>(base?: I): SegmentedItem {
    return SegmentedItem.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SegmentedItem>, I>>(object: I): SegmentedItem {
    const message = createBaseSegmentedItem();
    message.image = (object.image !== undefined && object.image !== null) ? Image.fromPartial(object.image) : undefined;
    message.link = object.link ?? undefined;
    message.meta = (object.meta !== undefined && object.meta !== null)
      ? ProductMeta.fromPartial(object.meta)
      : undefined;
    message.segmented = (object.segmented !== undefined && object.segmented !== null)
      ? SegmentedItem_SegmentedLinks.fromPartial(object.segmented)
      : undefined;
    return message;
  },
};

function createBaseSegmentedItem_SegmentedLinks(): SegmentedItem_SegmentedLinks {
  return { links: [] };
}

export const SegmentedItem_SegmentedLinks = {
  fromJSON(object: any): SegmentedItem_SegmentedLinks {
    return { links: Array.isArray(object?.links) ? object.links.map((e: any) => SegmentedLink.fromJSON(e)) : [] };
  },

  toJSON(message: SegmentedItem_SegmentedLinks): unknown {
    const obj: any = {};
    if (message.links) {
      obj.links = message.links.map((e) => e ? SegmentedLink.toJSON(e) : undefined);
    } else {
      obj.links = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SegmentedItem_SegmentedLinks>, I>>(base?: I): SegmentedItem_SegmentedLinks {
    return SegmentedItem_SegmentedLinks.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<SegmentedItem_SegmentedLinks>, I>>(object: I): SegmentedItem_SegmentedLinks {
    const message = createBaseSegmentedItem_SegmentedLinks();
    message.links = object.links?.map((e) => SegmentedLink.fromPartial(e)) || [];
    return message;
  },
};

function createBaseCategory(): Category {
  return { name: "", nameEn: "" };
}

export const Category = {
  fromJSON(object: any): Category {
    return {
      name: isSet(object.name) ? String(object.name) : "",
      nameEn: isSet(object.nameEn) ? String(object.nameEn) : "",
    };
  },

  toJSON(message: Category): unknown {
    const obj: any = {};
    message.name !== undefined && (obj.name = message.name);
    message.nameEn !== undefined && (obj.nameEn = message.nameEn);
    return obj;
  },

  create<I extends Exact<DeepPartial<Category>, I>>(base?: I): Category {
    return Category.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Category>, I>>(object: I): Category {
    const message = createBaseCategory();
    message.name = object.name ?? "";
    message.nameEn = object.nameEn ?? "";
    return message;
  },
};

function createBaseApp(): App {
  return {};
}

export const App = {
  fromJSON(_: any): App {
    return {};
  },

  toJSON(_: App): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<App>, I>>(base?: I): App {
    return App.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<App>, I>>(_: I): App {
    const message = createBaseApp();
    return message;
  },
};

function createBasePaymentShippingInfo(): PaymentShippingInfo {
  return {
    type: 0,
    desc: undefined,
    image: undefined,
    threshold: "",
    thresholdDiff: "",
    fee: "",
    promotionFee: "",
    overLimitRemind: "",
    noPickUpRemind: "",
    actualFee: "",
    total: "",
    totalUsd: "",
  };
}

export const PaymentShippingInfo = {
  fromJSON(object: any): PaymentShippingInfo {
    return {
      type: isSet(object.type) ? salesOrder_PaymentTypeFromJSON(object.type) : 0,
      desc: isSet(object.desc) ? String(object.desc) : undefined,
      image: isSet(object.image) ? Image.fromJSON(object.image) : undefined,
      threshold: isSet(object.threshold) ? String(object.threshold) : "",
      thresholdDiff: isSet(object.thresholdDiff) ? String(object.thresholdDiff) : "",
      fee: isSet(object.fee) ? String(object.fee) : "",
      promotionFee: isSet(object.promotionFee) ? String(object.promotionFee) : "",
      overLimitRemind: isSet(object.overLimitRemind) ? String(object.overLimitRemind) : "",
      noPickUpRemind: isSet(object.noPickUpRemind) ? String(object.noPickUpRemind) : "",
      actualFee: isSet(object.actualFee) ? String(object.actualFee) : "",
      total: isSet(object.total) ? String(object.total) : "",
      totalUsd: isSet(object.totalUsd) ? String(object.totalUsd) : "",
    };
  },

  toJSON(message: PaymentShippingInfo): unknown {
    const obj: any = {};
    message.type !== undefined && (obj.type = salesOrder_PaymentTypeToJSON(message.type));
    message.desc !== undefined && (obj.desc = message.desc);
    message.image !== undefined && (obj.image = message.image ? Image.toJSON(message.image) : undefined);
    message.threshold !== undefined && (obj.threshold = message.threshold);
    message.thresholdDiff !== undefined && (obj.thresholdDiff = message.thresholdDiff);
    message.fee !== undefined && (obj.fee = message.fee);
    message.promotionFee !== undefined && (obj.promotionFee = message.promotionFee);
    message.overLimitRemind !== undefined && (obj.overLimitRemind = message.overLimitRemind);
    message.noPickUpRemind !== undefined && (obj.noPickUpRemind = message.noPickUpRemind);
    message.actualFee !== undefined && (obj.actualFee = message.actualFee);
    message.total !== undefined && (obj.total = message.total);
    message.totalUsd !== undefined && (obj.totalUsd = message.totalUsd);
    return obj;
  },

  create<I extends Exact<DeepPartial<PaymentShippingInfo>, I>>(base?: I): PaymentShippingInfo {
    return PaymentShippingInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<PaymentShippingInfo>, I>>(object: I): PaymentShippingInfo {
    const message = createBasePaymentShippingInfo();
    message.type = object.type ?? 0;
    message.desc = object.desc ?? undefined;
    message.image = (object.image !== undefined && object.image !== null) ? Image.fromPartial(object.image) : undefined;
    message.threshold = object.threshold ?? "";
    message.thresholdDiff = object.thresholdDiff ?? "";
    message.fee = object.fee ?? "";
    message.promotionFee = object.promotionFee ?? "";
    message.overLimitRemind = object.overLimitRemind ?? "";
    message.noPickUpRemind = object.noPickUpRemind ?? "";
    message.actualFee = object.actualFee ?? "";
    message.total = object.total ?? "";
    message.totalUsd = object.totalUsd ?? "";
    return message;
  },
};

function createBasePaymentShippingOverseaInfo(): PaymentShippingOverseaInfo {
  return { name: "", englishName: "", phoneCountryCode: "", info: undefined };
}

export const PaymentShippingOverseaInfo = {
  fromJSON(object: any): PaymentShippingOverseaInfo {
    return {
      name: isSet(object.name) ? String(object.name) : "",
      englishName: isSet(object.englishName) ? String(object.englishName) : "",
      phoneCountryCode: isSet(object.phoneCountryCode) ? String(object.phoneCountryCode) : "",
      info: isSet(object.info) ? PaymentShippingInfo.fromJSON(object.info) : undefined,
    };
  },

  toJSON(message: PaymentShippingOverseaInfo): unknown {
    const obj: any = {};
    message.name !== undefined && (obj.name = message.name);
    message.englishName !== undefined && (obj.englishName = message.englishName);
    message.phoneCountryCode !== undefined && (obj.phoneCountryCode = message.phoneCountryCode);
    message.info !== undefined && (obj.info = message.info ? PaymentShippingInfo.toJSON(message.info) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<PaymentShippingOverseaInfo>, I>>(base?: I): PaymentShippingOverseaInfo {
    return PaymentShippingOverseaInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<PaymentShippingOverseaInfo>, I>>(object: I): PaymentShippingOverseaInfo {
    const message = createBasePaymentShippingOverseaInfo();
    message.name = object.name ?? "";
    message.englishName = object.englishName ?? "";
    message.phoneCountryCode = object.phoneCountryCode ?? "";
    message.info = (object.info !== undefined && object.info !== null)
      ? PaymentShippingInfo.fromPartial(object.info)
      : undefined;
    return message;
  },
};

function createBaseAnnouncement(): Announcement {
  return { id: "", link: "", content: "" };
}

export const Announcement = {
  fromJSON(object: any): Announcement {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      link: isSet(object.link) ? String(object.link) : "",
      content: isSet(object.content) ? String(object.content) : "",
    };
  },

  toJSON(message: Announcement): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.link !== undefined && (obj.link = message.link);
    message.content !== undefined && (obj.content = message.content);
    return obj;
  },

  create<I extends Exact<DeepPartial<Announcement>, I>>(base?: I): Announcement {
    return Announcement.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Announcement>, I>>(object: I): Announcement {
    const message = createBaseAnnouncement();
    message.id = object.id ?? "";
    message.link = object.link ?? "";
    message.content = object.content ?? "";
    return message;
  },
};

function createBaseRecipientAddress(): RecipientAddress {
  return { id: "", recipient: undefined, isDefault: false };
}

export const RecipientAddress = {
  fromJSON(object: any): RecipientAddress {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      recipient: isSet(object.recipient) ? Recipient.fromJSON(object.recipient) : undefined,
      isDefault: isSet(object.isDefault) ? Boolean(object.isDefault) : false,
    };
  },

  toJSON(message: RecipientAddress): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.recipient !== undefined &&
      (obj.recipient = message.recipient ? Recipient.toJSON(message.recipient) : undefined);
    message.isDefault !== undefined && (obj.isDefault = message.isDefault);
    return obj;
  },

  create<I extends Exact<DeepPartial<RecipientAddress>, I>>(base?: I): RecipientAddress {
    return RecipientAddress.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<RecipientAddress>, I>>(object: I): RecipientAddress {
    const message = createBaseRecipientAddress();
    message.id = object.id ?? "";
    message.recipient = (object.recipient !== undefined && object.recipient !== null)
      ? Recipient.fromPartial(object.recipient)
      : undefined;
    message.isDefault = object.isDefault ?? false;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var tsProtoGlobalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
